import { Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { VIDEO_MAX_SOURCE_MB } from "src/app/Services/video-upload.constants";
export type FlowchestAiImportSourceMode = "link" | "upload";

@Component({
  selector: "app-flowchest-ai-import-dialog",
  templateUrl: "./flowchest-ai-import-dialog.component.html",
  styleUrls: ["./flowchest-ai-import-dialog.component.scss"],
})
export class FlowchestAiImportDialogComponent implements OnInit {
  @ViewChild("videoFileInput") videoFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild("documentFileInput") documentFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild("consentSection") consentSection?: ElementRef<HTMLElement>;

  isMobileView = false;
  showRequirementsExpanded = false;
  showPolicyExpanded = false;
  consentHighlight = false;
  /** When false, only video file upload is shown (YouTube / hosted URL link hidden). */
  readonly showVideoLinkOption = false;

  sourceMode: FlowchestAiImportSourceMode = "upload";
  videoUrl = "";
  sourceTitle = "";
  sourceNotes = "";
  selectedFile: File | null = null;
  selectedFileName = "";
  selectedFileDurationSec = 0;
  selectedDocument: File | null = null;
  selectedDocumentName = "";
  includeDocumentInSupportingDocs = false;
  confirmRights = false;
  confirmPolicy = false;
  limitStepsTo30Sec = true;
  urlError = "";

  readonly maxUploadMb = VIDEO_MAX_SOURCE_MB;
  readonly maxDocumentMb = 10;

  /** Shown in the import dialog — keep in sync with API FlowchestAi:MaxVideoDurationMinutes (default 20). */
  readonly maxVideoDurationMinutes = 20;

  readonly privacyPolicyPoints = [
    "Flowchest AI processes your uploaded video to create step clips and AI-generated instructions.",
    "Step titles and instructions are generated using AI. Generated media is stored in your Flowchest account as a draft guide.",
    "Only import content you own or are authorized to use. Republishing third-party video without permission may violate copyright and our Terms.",
    "Do not submit private, sensitive, or personal data you do not want processed by Flowchest and our AI service providers.",
  ];

  readonly videoRequirements = [
    `Video length: up to ${this.maxVideoDurationMinutes} minutes (shorter tutorials work best, about 3–15 min)`,
    `Upload size: up to ${this.maxUploadMb} MB source (MP4, WebM, MOV, M4V) — large videos are optimized automatically before upload`,
    "How-to or tutorial content with clear steps works best",
    "You own the content or have permission to republish clips",
    "Flowchest AI creates 3–12 steps with titles, instructions, and step videos when processing succeeds",
  ];

  constructor(
    public dialogRef: MatDialogRef<FlowchestAiImportDialogComponent>,
    private toastr: ToastrService
  ) {}
  ngOnInit(): void {
    this.checkMobileView();
  }

  @HostListener("window:resize")
  onResize(): void {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;
  }

  toggleRequirements(): void {
    this.showRequirementsExpanded = !this.showRequirementsExpanded;
  }

  togglePolicy(): void {
    this.showPolicyExpanded = !this.showPolicyExpanded;
  }

  setSourceMode(mode: FlowchestAiImportSourceMode): void {
    this.sourceMode = mode;
    this.urlError = "";
    if (mode === "link") {
      this.clearSelectedDocument();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile = file;
    this.selectedFileName = file?.name ?? "";
    this.selectedFileDurationSec = 0;
    this.urlError = "";
    if (file) {
      this.probeVideoDuration(file);
    }
  }

  private probeVideoDuration(file: File): void {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        this.selectedFileDurationSec = Math.round(video.duration);
      }
      URL.revokeObjectURL(url);
    };
    video.onerror = () => URL.revokeObjectURL(url);
    video.src = url;
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.selectedFileName = "";
    this.selectedFileDurationSec = 0;
    if (this.videoFileInput?.nativeElement) {
      this.videoFileInput.nativeElement.value = "";
    }
  }

  onDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedDocument = file;
    this.selectedDocumentName = file?.name ?? "";
    this.includeDocumentInSupportingDocs = false;
    this.urlError = "";
    if (file && !this.isSupportedDocument(file)) {
      this.selectedDocument = null;
      this.selectedDocumentName = "";
      if (this.documentFileInput?.nativeElement) {
        this.documentFileInput.nativeElement.value = "";
      }
      this.urlError =
        "Unsupported document format. Upload .docx, .pptx, or .pdf (legacy .doc/.ppt not supported).";
    }
  }

  clearSelectedDocument(): void {
    this.selectedDocument = null;
    this.selectedDocumentName = "";
    this.includeDocumentInSupportingDocs = false;
    if (this.documentFileInput?.nativeElement) {
      this.documentFileInput.nativeElement.value = "";
    }
  }

  private isSupportedDocument(file: File): boolean {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    return ["docx", "pptx", "pdf"].includes(ext);
  }

  cancelToSelection(): void {
    this.dialogRef.close({ navigateTo: "selection" });
  }

  onConsentChange(): void {
    if (this.confirmRights && this.confirmPolicy) {
      this.consentHighlight = false;
      this.urlError = "";
    }
  }

  private scrollToConsentSection(): void {
    if (this.isMobileView) {
      this.showPolicyExpanded = true;
    }
    setTimeout(() => {
      this.consentSection?.nativeElement?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });
  }

  private remindConsentCheckboxes(): void {
    this.consentHighlight = true;
    this.scrollToConsentSection();

    if (!this.confirmRights && !this.confirmPolicy) {
      this.toastr.warning(
        "Please check both consent boxes before starting Flowchest AI.",
        "Consent required",
        { timeOut: 6000 }
      );
      return;
    }
    if (!this.confirmRights) {
      this.toastr.warning(
        "Please confirm you have permission to use and republish clips from this video.",
        "Consent required",
        { timeOut: 6000 }
      );
      return;
    }
    this.toastr.warning(
      "Please agree to the Privacy Policy and Terms & Conditions.",
      "Consent required",
      { timeOut: 6000 }
    );
  }

  private validateConsent(): boolean {
    if (!this.confirmRights) {
      this.urlError = "Please confirm you have rights to use this video.";
      this.remindConsentCheckboxes();
      return false;
    }
    if (!this.confirmPolicy) {
      this.urlError = "Please confirm you agree to the Privacy Policy and Terms.";
      this.remindConsentCheckboxes();
      return false;
    }
    return true;
  }
  submit(): void {
    this.urlError = "";
    if (!this.validateConsent()) {
      return;
    }

    const title = (this.sourceTitle || "").trim();

    if (this.sourceMode === "upload") {
      if (!this.selectedFile) {
        this.urlError = "Please choose a video file to upload.";
        return;
      }
      const ext = (this.selectedFile.name.split(".").pop() || "").toLowerCase();
      if (!["mp4", "webm", "mov", "m4v", "mkv"].includes(ext)) {
        this.urlError = "Unsupported format. Upload MP4, WebM, MOV, or M4V.";
        return;
      }
      if (this.selectedDocument && this.selectedDocument.size > this.maxDocumentMb * 1024 * 1024) {
        this.urlError = `Reference document is too large. Maximum size is ${this.maxDocumentMb} MB.`;
        return;
      }

      this.dialogRef.close({
        videoFile: this.selectedFile,
        sourceDocument: this.selectedDocument,
        includeDocumentInSupportingDocs: this.selectedDocument
          ? this.includeDocumentInSupportingDocs
          : false,
        sourceTitle: title,
        sourceNotes: (this.sourceNotes || "").trim(),
        videoDurationSec: this.selectedFileDurationSec,
        confirmRights: true,
        limitStepsTo30Sec: this.limitStepsTo30Sec,
      });
      return;
    }

    const url = (this.videoUrl || "").trim();
    if (!url) {
      this.urlError = "Please enter a video link.";
      return;
    }
    if (!this.isSupportedVideoUrl(url)) {
      this.urlError =
        "Enter a YouTube link or a direct hosted video URL (.mp4, .webm, .mov) or Flowchest media link.";
      return;
    }

    this.dialogRef.close({
      videoUrl: url,
      sourceTitle: title,
      sourceNotes: (this.sourceNotes || "").trim(),
      confirmRights: true,
    });
  }

  private isSupportedVideoUrl(url: string): boolean {
    if (/youtube\.com\/(watch|shorts|embed|live)|youtu\.be\//i.test(url)) {
      return true;
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return false;
      }
      const path = parsed.pathname.toLowerCase();
      if (/\.(mp4|webm|mov|m4v|mkv|ogv)$/.test(path)) {
        return true;
      }
      if (
        parsed.host.includes("blob.core.windows.net") &&
        path.includes("mediavideo")
      ) {
        return true;
      }
      return (
        path.includes("/video/") ||
        path.includes("/videos/") ||
        parsed.search.toLowerCase().includes("video")
      );
    } catch {
      return false;
    }
  }
}
